import { Component, OnInit, ElementRef, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { CompanySettings } from '../settings/model/company-settings';
import * as fromCompanySettingsStore from '../settings/store';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import { LocaleUtils } from 'localeselect/locale';

import { RoutePaths } from '../shared/navigation/route-paths';
import { mapUnsupportedLanguage } from './account.helper';
import { FeatureFlag } from '../settings/dto/feature-flag';
import { IntercomService } from '../shared/intercom/intercom.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'ls-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss']
})
export class AccountComponent implements OnInit {
  private lastCancelEventTrackedAt = 0;

  constructor(
    private el: ElementRef,
    private store: Store<fromCompanySettingsStore.CompanyState>,
    private router: Router,
    private intercom: IntercomService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.addMerchantAccountSpace();
  }

  private async addMerchantAccountSpace(): Promise<void> {
    const scriptUrl = environment.masUIscriptUrl;
    this.loadExternalScript(scriptUrl)
      .then(() => {
        fromCompanySettingsStore
          .getSettingsFromStore(this.store)
          .pipe(
            map((companySettings: CompanySettings) => {
              return companySettings;
            })
          )
          .subscribe(async companySettings => {
            // Check the feature flag
            if (companySettings.featureFlagMasUi === FeatureFlag.DISABLED) {
              // Redirect to the old account page
              this.router.navigate(['/account']);
              return;
            }

            const masterCompanyId = companySettings.masterCompanyId;
            const merchantAccountSpace = document.createElement(
              'merchant-account-space'
            );

            const lsPage = this.el.nativeElement.querySelector('ls-page > div');
            lsPage.appendChild(merchantAccountSpace);

            const locale = LocaleUtils.getLocale();
            const mappedLocale = mapUnsupportedLanguage(locale);
            const baseUrl = `${
              location.origin.includes('localhost')
                ? location.origin
                : location.origin.replace('-web', '')
            }/PosServer/rest/manager/v1/mas-proxy`;

            (window as any).getLightspeedToken().then(res => {
              if (merchantAccountSpace) {
                const handleError = (event: Event) => {
                  console.error('MAS UI Error:', (event as CustomEvent).detail);
                };

                merchantAccountSpace.addEventListener(
                  'merchant-account-space:error',
                  handleError
                );

                (merchantAccountSpace as any).init({
                  // Required options
                  baseUrl,
                  productCode: 'lsl',
                  entityType: 'location',
                  entityId: masterCompanyId.toString(),
                  locale: mappedLocale,
                  customHeaders: {
                    'requires-config-before-render': 'true',
                    ...(environment.masTestMode
                      ? {
                          'X-mas-test-mode': 'true'
                        }
                      : {}),
                    'X-Auth-Token': res.token
                  },

                  settings: {
                    overview: {
                      onDeviceInUseClick: () => {
                        this.ngZone.run(() => {
                          this.router.navigate([RoutePaths.DEVICES]);
                        });
                      },
                      onChatSupportClick: () => {
                        this.intercom.open();
                      },
                      ...(environment.masShowCancelSubscriptionButton
                        ? {
                            onCancelSubscriptionClick: () => {
                              this.handleCancelSubscriptionClick();
                            }
                          }
                        : {})
                    }
                  }
                });
              }
            });

            this.el.nativeElement.querySelector('.o-layout').style.display =
              'none';
          });
      })
      .catch(error => {
        console.error('Error loading script:', error);
      });
  }

  public handleCancelSubscriptionClick(): void {
    const now = Date.now();
    const oneMinute = 60 * 1000;

    // Throttle the Intercom 'cancel_subscription' event to a maximum of once per minute
    // to prevent spamming analytics if the user clicks multiple times in rapid succession.
    if (now - this.lastCancelEventTrackedAt >= oneMinute) {
      this.intercom.trackEvent('cancel_subscription', {
        cancellation_date: Math.floor(now / 1000),
        reason: 'user_request',
        productCode: 'lsl'
      });
      this.lastCancelEventTrackedAt = now;
    }

    this.intercom.open();
  }

  private loadExternalScript(scriptUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const scriptElement = document.createElement('script');
      scriptElement.type = 'module';
      scriptElement.src = scriptUrl;
      scriptElement.onload = () => resolve();
      scriptElement.onerror = error => reject(error);
      document.head.appendChild(scriptElement);
    });
  }
}
